import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Alert,
  TablePagination,
  TableSortLabel,
  CircularProgress,
  Autocomplete,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import api from '../../services/api';
import Layout from '../Layout';

const EmpresaSeguradoraPanel = () => {
  // Estados principais
  const [mapeamentos, setMapeamentos] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [seguradoras, setSeguradoras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMapeamento, setEditingMapeamento] = useState(null);
  const [error, setError] = useState('');
  const [dialogError, setDialogError] = useState('');
  const [success, setSuccess] = useState('');

  // Estados do formulário
  const [formData, setFormData] = useState({
    empresa_id: '',
    seguradora_id: '',
    razao_social_seguradora: ''
  });

  // Estados de paginação e pesquisa
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [orderBy, setOrderBy] = useState('empresa_nome');
  const [order, setOrder] = useState('asc');
  // Filtros adicionais
  const [filtroSeguradora, setFiltroSeguradora] = useState(null);
  const [filtroRazao, setFiltroRazao] = useState('');

  // Carregar dados
  const fetchMapeamentos = async () => {
    try {
      setLoading(true);
      const response = await api.get('/empresa-seguradora');
      const raw = response.data;
      const lista = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.data)
          ? raw.data
          : Array.isArray(raw?.results)
            ? raw.results
            : [];
      setMapeamentos(lista);
    } catch (err) {
      console.error('Erro ao buscar mapeamentos:', err);
      setError('Erro ao carregar mapeamentos. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmpresas = async () => {
    try {
      const response = await api.get('/empresas');
      const raw = response.data;
      const lista = Array.isArray(raw) ? raw : Array.isArray(raw?.empresas) ? raw.empresas : [];
      const byId = new Map();
      for (let i = 0; i < lista.length; i++) {
        const e = lista[i];
        const id = e?.id ?? e?.ID ?? e?.Id;
        if (!id) continue;
        const nomeRaw = (
          e?.["Nome Fantasia"] ??
          e?.NomeFantasia ??
          e?.RazaoSocial ??
          e?.razao_social ??
          e?.nome ??
          e?.Nome ??
          id
        );
        const nomeFantasia = String(nomeRaw).trim();
        const nome = `${id} - ${nomeFantasia}`; // Formato: "ID - NOME"
        if (!byId.has(id)) byId.set(id, { id, nome, nomeFantasia, displayLabel: `[${id}] ${nomeFantasia}` });
      }
      const normalizadas = Array.from(byId.values()).sort((a, b) => a.id - b.id); // Ordenar por ID
      setEmpresas(normalizadas);
    } catch (err) {
      console.error('[EmpresaSeguradoraPanel] Erro ao buscar empresas:', err);
    }
  };

  const fetchSeguradoras = async () => {
    try {
      const response = await api.get('/seguradoras');
      const raw = response.data;
      const lista = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.data)
          ? raw.data
          : Array.isArray(raw?.results)
            ? raw.results
            : [];
      const byId = new Map();
      for (const s of lista) {
        const id = s?.id ?? s?.ID;
        if (!id) continue;
        const nome = (s?.nome ?? s?.Nome ?? '').toString().trim() || `Seguradora ${id}`;
        if (!byId.has(id)) byId.set(id, { id, nome });
      }
      const normalizadas = Array.from(byId.values()).sort((a, b) => a.nome.localeCompare(b.nome));
      setSeguradoras(normalizadas);
    } catch (err) {
      console.error('Erro ao buscar seguradoras:', err);
    }
  };

  useEffect(() => {
    Promise.all([
      fetchMapeamentos(),
      fetchEmpresas(),
      fetchSeguradoras()
    ]);
  }, []);

  // Handlers do formulário
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      empresa_id: '',
      seguradora_id: '',
      razao_social_seguradora: ''
    });
    setEditingMapeamento(null);
  };

  const handleOpenDialog = (mapeamento = null) => {
    setDialogError(''); // Limpar erro do diálogo
    if (mapeamento) {
      setEditingMapeamento(mapeamento);
      setFormData({
        empresa_id: mapeamento.empresa_id || '',
        seguradora_id: mapeamento.seguradora_id || '',
        razao_social_seguradora: mapeamento.razao_social_seguradora || ''
      });
    } else {
      resetForm();
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    resetForm();
    setError('');
    setDialogError(''); // Limpar erro do diálogo
  };

  // Salvar mapeamento
  const handleSave = async () => {
    try {
      if (!formData.empresa_id) {
        setDialogError('Empresa é obrigatória');
        return;
      }

      if (!formData.seguradora_id) {
        setDialogError('Seguradora é obrigatória');
        return;
      }

      if (!formData.razao_social_seguradora.trim()) {
        setDialogError('Razão social da seguradora é obrigatória');
        return;
      }

      const payload = {
        empresa_id: parseInt(formData.empresa_id),
        seguradora_id: parseInt(formData.seguradora_id),
        razao_social_seguradora: formData.razao_social_seguradora.trim()
      };

      // Validação de duplicata no frontend (antes de enviar para o backend)
      if (!editingMapeamento) {
        const empresaSelecionada = empresas.find(emp => emp.id === parseInt(formData.empresa_id));
        const seguradoraSelecionada = seguradoras.find(seg => seg.id === parseInt(formData.seguradora_id));
        
        const duplicataExistente = mapeamentos.find(map => 
          map.empresa_id === parseInt(formData.empresa_id) && 
          map.seguradora_id === parseInt(formData.seguradora_id)
        );

        if (duplicataExistente) {
          const nomeEmpresa = empresaSelecionada?.nomeFantasia || formData.empresa_id;
          const nomeSeguradora = seguradoraSelecionada?.nome || formData.seguradora_id;
          setDialogError(`Já existe um mapeamento para a empresa "${nomeEmpresa}" com a seguradora "${nomeSeguradora}". Cada empresa pode ter apenas um mapeamento por seguradora.`);
          return;
        }
      } else {
        // Na edição, verificar se não está criando duplicata com outro registro
        const duplicataExistente = mapeamentos.find(map => 
          map.id !== editingMapeamento.id &&
          map.empresa_id === parseInt(formData.empresa_id) && 
          map.seguradora_id === parseInt(formData.seguradora_id)
        );

        if (duplicataExistente) {
          const empresaSelecionada = empresas.find(emp => emp.id === parseInt(formData.empresa_id));
          const seguradoraSelecionada = seguradoras.find(seg => seg.id === parseInt(formData.seguradora_id));
          const nomeEmpresa = empresaSelecionada?.nomeFantasia || formData.empresa_id;
          const nomeSeguradora = seguradoraSelecionada?.nome || formData.seguradora_id;
          setDialogError(`Já existe um mapeamento para a empresa "${nomeEmpresa}" com a seguradora "${nomeSeguradora}". Cada empresa pode ter apenas um mapeamento por seguradora.`);
          return;
        }
      }

      if (editingMapeamento) {
        await api.put(`/empresa-seguradora/${editingMapeamento.id}`, payload);
        setSuccess('Mapeamento atualizado com sucesso!');
      } else {
        await api.post('/empresa-seguradora', payload);
        setSuccess('Mapeamento criado com sucesso!');
      }

      handleCloseDialog();
      fetchMapeamentos();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Erro ao salvar mapeamento:', err);
      // Se é erro 409 (conflito), mostrar mensagem mais amigável
      if (err.response?.status === 409) {
        setDialogError('Esta empresa já possui um mapeamento com esta seguradora. Cada empresa pode ter apenas um mapeamento por seguradora.');
      } else {
        setDialogError(err.response?.data?.message || 'Erro ao salvar mapeamento');
      }
    }
  };

  // Excluir mapeamento
  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este mapeamento?')) {
      return;
    }

    try {
      await api.delete(`/empresa-seguradora/${id}`);
      setSuccess('Mapeamento excluído com sucesso!');
      fetchMapeamentos();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Erro ao excluir mapeamento:', err);
      setError(err.response?.data?.message || 'Erro ao excluir mapeamento');
    }
  };

  // Filtrar e ordenar dados
  const safeMapeamentos = Array.isArray(mapeamentos) ? mapeamentos : [];
  const filteredMapeamentos = safeMapeamentos
    .filter(map => {
      const term = searchTerm.trim().toLowerCase();
      const matchesIdOrEmpresa = term === '' ||
        String(map.id || '').includes(searchTerm) ||
        map.empresa_nome?.toLowerCase().includes(term);
      const matchesSeguradora = !filtroSeguradora || map.seguradora_id === filtroSeguradora.id;
      const matchesRazao = filtroRazao.trim() === '' ||
        map.razao_social_seguradora?.toLowerCase().includes(filtroRazao.trim().toLowerCase());
      return matchesIdOrEmpresa && matchesSeguradora && matchesRazao;
    })
    .sort((a, b) => {
      if (orderBy === 'id') {
        const diff = (a.id || 0) - (b.id || 0);
        return order === 'asc' ? diff : -diff;
      }
      const aValue = (a[orderBy] || '').toString();
      const bValue = (b[orderBy] || '').toString();
      const cmp = aValue.localeCompare(bValue);
      return order === 'asc' ? cmp : -cmp;
    });

  const paginatedMapeamentos = filteredMapeamentos.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };



  return (
    <Layout>
      <Container maxWidth="lg">
        <Box sx={{ py: 3 }}>
          <Typography variant="h5" gutterBottom>
            Mapeamentos Empresa-Seguradora
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure como cada empresa aparece nos arquivos de cada seguradora
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}

          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Pesquisar por ID ou Empresa"
                  variant="outlined"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    endAdornment: searchTerm && (
                      <IconButton onClick={() => setSearchTerm('')} size="small">
                        <ClearIcon />
                      </IconButton>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={Array.isArray(seguradoras) ? seguradoras : []}
                  getOptionLabel={(option) => option.nome || ''}
                  value={filtroSeguradora}
                  onChange={(event, newValue) => setFiltroSeguradora(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Filtrar por Seguradora"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Filtrar por Razão Social"
                  variant="outlined"
                  value={filtroRazao}
                  onChange={(e) => setFiltroRazao(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={1} sx={{ textAlign: 'right' }}>
                <Button
                  fullWidth
                  variant="contained"
                  color="success"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog()}
                >
                  Novo
                </Button>
              </Grid>
            </Grid>
          </Paper>

          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ width: 100, fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={orderBy === 'id'}
                        direction={orderBy === 'id' ? order : 'asc'}
                        onClick={() => handleRequestSort('id')}
                      >
                        ID
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'empresa_nome'}
                        direction={orderBy === 'empresa_nome' ? order : 'asc'}
                        onClick={() => handleRequestSort('empresa_nome')}
                      >
                        Empresa
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'seguradora_nome'}
                        direction={orderBy === 'seguradora_nome' ? order : 'asc'}
                        onClick={() => handleRequestSort('seguradora_nome')}
                      >
                        Seguradora
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'razao_social_seguradora'}
                        direction={orderBy === 'razao_social_seguradora' ? order : 'asc'}
                        onClick={() => handleRequestSort('razao_social_seguradora')}
                      >
                        Razão Social na Seguradora
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : paginatedMapeamentos.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="text.secondary">
                          {searchTerm ? 'Nenhum mapeamento encontrado' : 'Nenhum mapeamento cadastrado'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedMapeamentos.map((mapeamento) => (
                      <TableRow key={mapeamento.id} hover>
                        <TableCell>
                          <code>{mapeamento.id}</code>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={mapeamento.empresa_nome} 
                            variant="outlined" 
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={mapeamento.seguradora_nome} 
                            color="primary" 
                            variant="outlined" 
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" component="code" sx={{ backgroundColor: 'grey.100', p: 0.5, borderRadius: 1 }}>
                            {mapeamento.razao_social_seguradora}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            onClick={() => handleOpenDialog(mapeamento)}
                            color="primary"
                            size="small"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDelete(mapeamento.id)}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredMapeamentos.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage="Registros por página:"
              labelDisplayedRows={({ from, to, count }) => 
                `${from}-${to} de ${count}`
              }
            />
          </Paper>

          {/* Dialog para criar/editar */}
          <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
            <DialogTitle>
              {editingMapeamento ? 'Editar Mapeamento' : 'Novo Mapeamento'}
            </DialogTitle>
            <DialogContent>
              {dialogError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogError('')}>
                  {dialogError}
                </Alert>
              )}
              <Box sx={{ pt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      options={Array.isArray(empresas) ? empresas : []}
                      getOptionLabel={(option) => option.displayLabel || option.nome || ''}
                      value={(Array.isArray(empresas) ? empresas : []).find(e => e.id === parseInt(formData.empresa_id)) || null}
                      filterOptions={(options, { inputValue }) => {
                        const searchTerm = inputValue.toLowerCase();
                        return options.filter(option => 
                          String(option.id).includes(searchTerm) ||
                          (option.nomeFantasia || '').toLowerCase().includes(searchTerm) ||
                          (option.nome || '').toLowerCase().includes(searchTerm) ||
                          (option.displayLabel || '').toLowerCase().includes(searchTerm)
                        );
                      }}
                      onChange={(event, newValue) => {
                        setFormData(prev => ({
                          ...prev,
                          empresa_id: newValue ? newValue.id.toString() : ''
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Empresa"
                          required
                          helperText="Selecione a empresa do sistema"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      options={Array.isArray(seguradoras) ? seguradoras : []}
                      getOptionLabel={(option) => option.nome || ''}
                      value={(Array.isArray(seguradoras) ? seguradoras : []).find(s => s.id === parseInt(formData.seguradora_id)) || null}
                      onChange={(event, newValue) => {
                        setFormData(prev => ({
                          ...prev,
                          seguradora_id: newValue ? newValue.id.toString() : ''
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Seguradora"
                          required
                          helperText="Selecione a seguradora"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Razão Social na Seguradora"
                      name="razao_social_seguradora"
                      value={formData.razao_social_seguradora}
                      onChange={handleInputChange}
                      required
                      helperText="Como a empresa aparece nos arquivos desta seguradora"
                      multiline
                      rows={2}
                    />
                  </Grid>
                </Grid>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog} color="inherit">
                Cancelar
              </Button>
              <Button onClick={handleSave} variant="contained">
                {editingMapeamento ? 'Atualizar' : 'Criar'}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Container>
    </Layout>
  );
};

export default EmpresaSeguradoraPanel;
